/* ==========================================================================
   Spinner Component
   Task F27: Standalone spinner for inline loading states
   Based on design.md specifications
   ========================================================================== */

import { forwardRef, type HTMLAttributes } from 'react'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /** Spinner size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /** Color variant */
  variant?: 'primary' | 'white' | 'muted'
  /** Optional label for screen readers */
  label?: string
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

/**
 * Spinner component for inline loading indicators.
 * Uses a CSS border-based spinner with animation per design.md.
 *
 * @example
 * ```tsx
 * // Basic spinner
 * <Spinner />
 *
 * // Different sizes
 * <Spinner size="xs" />
 * <Spinner size="lg" />
 *
 * // Different colors
 * <Spinner variant="primary" />
 * <Spinner variant="white" />
 *
 * // With loading text
 * <div className="flex items-center gap-2">
 *   <Spinner size="sm" />
 *   <span>Loading...</span>
 * </div>
 * ```
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      size = 'md',
      variant = 'primary',
      label = 'Loading',
      className = '',
      ...props
    },
    ref
  ) => {
    // Size mappings
    const sizeStyles: Record<string, string> = {
      xs: 'w-3 h-3 border-[1.5px]',
      sm: 'w-4 h-4 border-2',
      md: 'w-5 h-5 border-2',
      lg: 'w-6 h-6 border-2',
      xl: 'w-8 h-8 border-[3px]',
    }

    // Color mappings
    const variantStyles: Record<string, string> = {
      primary: 'border-[var(--color-deep-purple)] border-t-transparent',
      white: 'border-white border-t-transparent',
      muted: 'border-[var(--color-gray-400)] border-t-transparent',
    }

    const classes = [
      'inline-block',
      'rounded-full',
      'animate-spin',
      sizeStyles[size],
      variantStyles[variant],
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div
        ref={ref}
        className={classes}
        role="status"
        aria-label={label}
        {...props}
      >
        <span className="sr-only">{label}</span>
      </div>
    )
  }
)

Spinner.displayName = 'Spinner'

/* --------------------------------------------------------------------------
   Spinner with Text
   -------------------------------------------------------------------------- */

export interface SpinnerWithTextProps extends SpinnerProps {
  /** Text to display next to the spinner */
  text?: string
  /** Position of text relative to spinner */
  textPosition?: 'right' | 'bottom'
}

/**
 * Spinner with accompanying text.
 *
 * @example
 * ```tsx
 * <SpinnerWithText text="Loading..." />
 * <SpinnerWithText text="Saving changes" textPosition="bottom" />
 * ```
 */
export function SpinnerWithText({
  text = 'Loading...',
  textPosition = 'right',
  size = 'sm',
  variant = 'primary',
  className = '',
  ...props
}: SpinnerWithTextProps) {
  const containerClasses =
    textPosition === 'right'
      ? 'flex items-center gap-2'
      : 'flex flex-col items-center gap-2'

  return (
    <div className={`${containerClasses} ${className}`}>
      <Spinner size={size} variant={variant} {...props} />
      <span className="text-sm text-[var(--color-gray-600)]">{text}</span>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Full Page Spinner
   -------------------------------------------------------------------------- */

export interface FullPageSpinnerProps extends SpinnerProps {
  /** Text to display below the spinner */
  text?: string
  /** Whether to show an overlay background */
  overlay?: boolean
}

/**
 * Full page loading spinner, centered in the viewport.
 *
 * @example
 * ```tsx
 * <FullPageSpinner />
 * <FullPageSpinner text="Loading your data..." />
 * <FullPageSpinner overlay /> // With semi-transparent overlay
 * ```
 */
export function FullPageSpinner({
  text,
  overlay = false,
  size = 'xl',
  variant = 'primary',
  ...props
}: FullPageSpinnerProps) {
  const containerClasses = overlay
    ? 'fixed inset-0 bg-white/80 z-50 flex flex-col items-center justify-center'
    : 'flex flex-col items-center justify-center min-h-[200px] py-12'

  return (
    <div className={containerClasses}>
      <Spinner size={size} variant={variant} {...props} />
      {text && (
        <p className="mt-4 text-sm text-[var(--color-gray-600)]">{text}</p>
      )}
    </div>
  )
}

export default Spinner
